import { Box, Typography } from "@mui/material";
import { useState, useEffect } from "react";
import { Button, TextField, Stack, IconButton, Checkbox, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import { addProductCurrencies, updateProductCurrencies, deleteProductCurrencies } from "../../../../../services/Admin/ProductsMediaAndDetailsApi";
import { getAdminCountries } from "../../../../../services/Admin/CountriesApi";

// استقبل productId و onSuccess للاستخدام
const ProductPricesTab = ({ productId, currencies = [], onSuccess }) => {
  const [newCurrencies, setNewCurrencies] = useState([]);
  const [editableCurrencies, setEditableCurrencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingValues, setEditingValues] = useState({ country_id: "", price: "", price_after_discount: "" });
  const [rowSaving, setRowSaving] = useState(false);
  const [selectedCurrencyIds, setSelectedCurrencyIds] = useState([]);
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await getAdminCountries();
        const data = res?.data?.data || res?.data || res || [];
        setCountries(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching countries:", err);
      }
    };
    fetchCountries();
  }, []);

  const getCountryName = (id) => {
    const country = countries.find((c) => c.id === Number(id));
    return country ? `${country.name_ar} (${country.currency_ar})` : id;
  };

  useEffect(() => {
    setEditableCurrencies(
      Array.isArray(currencies)
        ? currencies.map((c) => ({
            id: c.id,
            country_id: c.country_id ?? "",
            price: c.price ?? "",
            price_after_discount: c.price_after_discount ?? "",
          }))
        : []
    );
  }, [currencies]);

  const handleAddRow = () => {
    setNewCurrencies((s) => [...s, { country_id: "", price: "", price_after_discount: "" }]);
  };

  const handleRemove = (index) => {
    setNewCurrencies((s) => s.filter((_, i) => i !== index));
  };

  const handleChange = (index, field, value) => {
    setNewCurrencies((s) => s.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  const handleExistingChange = (index, field, value) => {
    setEditableCurrencies((s) => s.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  const handleUpdateSubmit = async () => {
    setError("");
    if (!productId) {
      setError("productId مطلوب.");
      return;
    }
    if (!editableCurrencies.length) {
      setError("لا توجد أسعار للتعديل.");
      return;
    }
    setLoading(true);
    try {
      const formatted = editableCurrencies.map((c) => ({
        id: c.id,
        country_id: Number(c.country_id),
        price: Number(c.price),
        price_after_discount: Number(c.price_after_discount || 0),
      }));
      await updateProductCurrencies(formatted, { product_id: productId });
      if (onSuccess) await onSuccess();
    } catch (err) {
      setError(err?.message || "فشل تحديث الأسعار");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setError("");
    if (!productId) {
      setError("productId مطلوب.");
      return;
    }
    if (!newCurrencies.length) {
      setError("أضف سعرًا واحدًا على الأقل.");
      return;
    }
    // تحويل القيم النصية إلى أرقام
    const formatted = newCurrencies.map((c) => ({
      country_id: Number(c.country_id),
      price: Number(c.price),
      price_after_discount: Number(c.price_after_discount || 0),
    }));

    setLoading(true);
    try {
      await addProductCurrencies(productId, formatted, { product_id: productId });
      setNewCurrencies([]);
      if (onSuccess) await onSuccess();
    } catch (err) {
      setError(err?.message || "فشل إضافة الأسعار");
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    if (!id) return;
    setSelectedCurrencyIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const handleDeleteSingle = async (id) => {
    setError("");
    if (!productId) {
      setError("productId مطلوب.");
      return;
    }
    if (!id) return;
    setLoading(true);
    try {
      await deleteProductCurrencies([id], { product_id: productId });
      setEditableCurrencies((s) => s.filter((x) => x.id !== id));
      setSelectedCurrencyIds((s) => s.filter((x) => x !== id));
      if (onSuccess) await onSuccess();
    } catch (err) {
      setError(err?.message || "فشل حذف السعر");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    setError("");
    if (!productId) {
      setError("productId مطلوب.");
      return;
    }
    if (!selectedCurrencyIds.length) {
      setError("اختر سعرًا واحدًا على الأقل للحذف.");
      return;
    }
    setLoading(true);
    try {
      await deleteProductCurrencies(selectedCurrencyIds, { product_id: productId });
      setEditableCurrencies((s) => s.filter((x) => !selectedCurrencyIds.includes(x.id)));
      setSelectedCurrencyIds([]);
      if (onSuccess) await onSuccess();
    } catch (err) {
      setError(err?.message || "فشل حذف الأسعار");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography sx={{ fontWeight: 600, mb: 1 }}>أسعار حسب الدولة</Typography>

      {/* عرض وتحرير الأسعار الحالية */}
      {editableCurrencies && editableCurrencies.length ? (
        <>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Button onClick={handleUpdateSubmit} disabled={loading || !editableCurrencies.length || !productId} size="small" variant="contained">
              {loading ? "جارٍ..." : "حفظ التعديلات"}
            </Button>
            <Button
              onClick={handleBulkDelete}
              disabled={loading || !selectedCurrencyIds.length || !productId}
              size="small"
              variant="outlined"
              color="error"
            >
              حذف المحدد ({selectedCurrencyIds.length})
            </Button>
          </Stack>

          {editableCurrencies.map((c, idx) => {
            const isEditing = editingId === c.id;
            return (
              <Stack key={c.id ?? idx} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                {!isEditing ? (
                  <>
                    <Checkbox checked={selectedCurrencyIds.includes(c.id)} onChange={() => toggleSelect(c.id)} disabled={!c.id} size="small" />
                    <Typography sx={{ minWidth: 100 }}>{`الدولة: ${getCountryName(c.country_id)}`}</Typography>
                    <Typography sx={{ minWidth: 100 }}>{`السعر: ${c.price}`}</Typography>
                    <Typography sx={{ minWidth: 140 }}>{`بعد الخصم: ${c.price_after_discount ?? 0}`}</Typography>
                    <IconButton size="small" onClick={() => {
                      setEditingId(c.id);
                      setEditingValues({
                        country_id: c.country_id ?? "",
                        price: c.price ?? "",
                        price_after_discount: c.price_after_discount ?? "",
                      });
                    }} aria-label="edit">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteSingle(c.id)} aria-label="delete-existing" disabled={!c.id || loading || !productId}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel>الدولة</InputLabel>
                      <Select
                        value={editingValues.country_id}
                        onChange={(e) => setEditingValues((p) => ({ ...p, country_id: e.target.value }))}
                        label="الدولة"
                      >
                        {countries.map((country) => (
                          <MenuItem key={country.id} value={country.id}>
                            {country.name_ar} ({country.currency_ar})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField size="small" label="السعر" type="number" value={editingValues.price} onChange={(e) => setEditingValues((p) => ({ ...p, price: e.target.value }))} />
                    <TextField size="small" label="السعر بعد الخصم" type="number" value={editingValues.price_after_discount} onChange={(e) => setEditingValues((p) => ({ ...p, price_after_discount: e.target.value }))} />
                    <IconButton size="small" onClick={async () => {
                      // حفظ صف واحد
                      setRowSaving(true);
                      try {
                        const payload = [{
                          id: c.id,
                          country_id: Number(editingValues.country_id),
                          price: Number(editingValues.price),
                          price_after_discount: Number(editingValues.price_after_discount || 0),
                        }];
                        await updateProductCurrencies(payload, { product_id: productId });
                        setEditingId(null);
                        setEditingValues({ country_id: "", price: "", price_after_discount: "" });
                        if (onSuccess) await onSuccess();
                      } catch (err) {
                        setError(err?.message || "فشل حفظ الصف");
                      } finally {
                        setRowSaving(false);
                      }
                    }} disabled={rowSaving || !productId} aria-label="save">
                      <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => { setEditingId(null); setEditingValues({ country_id: "", price: "", price_after_discount: "" }); }} aria-label="cancel">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Stack>
            );
          })}
        </>
      ) : (
        <Typography color="text.secondary">لا توجد أسعار</Typography>
      )}

      {/* إضافة أسعار جديدة */}
      <Box sx={{ mt: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Button startIcon={<AddIcon />} onClick={handleAddRow} size="small" variant="contained">
            إضافة صف
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !newCurrencies.length || !productId}
            size="small"
            variant="outlined"
          >
            {loading ? "جارٍ..." : "حفظ الأسعار"}
          </Button>
        </Stack>

        {error ? <Typography color="error" sx={{ mb: 1 }}>{error}</Typography> : null}

        {newCurrencies.map((r, idx) => (
          <Stack key={idx} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>الدولة</InputLabel>
              <Select
                value={r.country_id}
                onChange={(e) => handleChange(idx, "country_id", e.target.value)}
                label="الدولة"
              >
                {countries.map((country) => (
                  <MenuItem key={country.id} value={country.id}>
                    {country.name_ar} ({country.currency_ar})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="السعر"
              type="number"
              value={r.price}
              onChange={(e) => handleChange(idx, "price", e.target.value)}
            />
            <TextField
              size="small"
              label="السعر بعد الخصم"
              type="number"
              value={r.price_after_discount}
              onChange={(e) => handleChange(idx, "price_after_discount", e.target.value)}
            />
            <IconButton size="small" onClick={() => handleRemove(idx)} aria-label="remove">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
      </Box>
    </Box>
  );
};

export default ProductPricesTab;
